import {
  type ComputedRef,
  type Ref,
  computed,
  getCurrentScope,
  onScopeDispose,
  ref,
  watch,
} from 'vue'

interface UseTypewriterOptions {
  minDelay?: number
  maxDelay?: number
  catchUpThreshold?: number
}

interface UseTypewriterReturn {
  displayed: ComputedRef<string>
  drain: () => Promise<void>
  reset: () => void
}

const WHITESPACE = /\s/
const CODE_FENCE = '```'

// Fastest per-tick delay (ms), reached when many chars are queued.
const DEFAULT_MIN_DELAY = 15
// Slowest per-tick delay (ms), reached when typewriter caught up.
const DEFAULT_MAX_DELAY = 55
// Lag (in chars) above which delay clamps to minDelay; saturation point is
// catchUpThreshold * 5. Lower threshold = sooner speed-up.
const DEFAULT_CATCH_UP_THRESHOLD = 20

function shouldUseLineGranularity(text: string, index: number): boolean {
  let lineStart = index
  while (lineStart > 0 && text[lineStart - 1] !== '\n') lineStart -= 1

  if (text.startsWith(CODE_FENCE, lineStart)) return true
  if (text.charAt(lineStart) === '|') return true

  let count = 0
  let i = 0
  while (i < lineStart) {
    if (text.startsWith(CODE_FENCE, i)) {
      count += 1
      i += CODE_FENCE.length
    } else {
      i += 1
    }
  }

  return count % 2 === 1
}

function nextTokenIndex(text: string, from: number): number {
  if (from >= text.length) return text.length

  if (shouldUseLineGranularity(text, from)) {
    const newline = text.indexOf('\n', from)

    return newline === -1 ? text.length : newline + 1
  }

  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'word' })
    const iter = segmenter.segment(text.slice(from))[Symbol.iterator]()
    const first = iter.next()
    if (!first.done) {
      let advance = first.value.segment.length
      const next = iter.next()
      if (!next.done && !next.value.isWordLike) {
        advance += next.value.segment.length
      }

      return from + advance
    }
  }

  let wordEnd = from
  while (wordEnd < text.length && !WHITESPACE.test(text.charAt(wordEnd))) {
    wordEnd += 1
  }
  let i = wordEnd
  while (i < text.length && WHITESPACE.test(text.charAt(i))) i += 1

  return i > from ? i : from + 1
}

export function useTypewriter(
  source: Ref<string>,
  options: UseTypewriterOptions = {},
): UseTypewriterReturn {
  const minDelay = options.minDelay ?? DEFAULT_MIN_DELAY
  const maxDelay = options.maxDelay ?? DEFAULT_MAX_DELAY
  const catchUpThreshold =
    options.catchUpThreshold ?? DEFAULT_CATCH_UP_THRESHOLD

  const displayedIndex = ref(0)
  const displayed = computed(() => source.value.slice(0, displayedIndex.value))

  let timer: ReturnType<typeof setTimeout> | null = null
  let drainResolvers: Array<() => void> = []

  function computeDelay(): number {
    const lag = source.value.length - displayedIndex.value
    const ratio = Math.min(1, Math.max(0, lag / (catchUpThreshold * 5)))

    return Math.round(maxDelay - ratio * (maxDelay - minDelay))
  }

  function flushDrain() {
    if (drainResolvers.length === 0) return
    const resolvers = drainResolvers
    drainResolvers = []
    for (const resolve of resolvers) resolve()
  }

  function tick() {
    timer = null
    displayedIndex.value = nextTokenIndex(source.value, displayedIndex.value)

    if (displayedIndex.value < source.value.length) {
      schedule()
    } else {
      flushDrain()
    }
  }

  function schedule() {
    if (timer !== null) return
    if (displayedIndex.value >= source.value.length) {
      flushDrain()

      return
    }
    timer = setTimeout(tick, computeDelay())
  }

  watch(
    source,
    (next) => {
      if (next.length < displayedIndex.value) {
        displayedIndex.value = next.length
        if (next.length === 0) flushDrain()
      }
      schedule()
    },
    { immediate: true },
  )

  function drain(): Promise<void> {
    if (displayedIndex.value >= source.value.length) {
      return Promise.resolve()
    }

    return new Promise<void>((resolve) => {
      drainResolvers.push(resolve)
    })
  }

  function reset() {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    displayedIndex.value = 0
    flushDrain()
  }

  if (getCurrentScope()) {
    onScopeDispose(() => {
      if (timer !== null) clearTimeout(timer)
      drainResolvers = []
    })
  }

  return { displayed, drain, reset }
}
